import React, { ReactNode, useMemo, useState } from 'react'
import { FaCheckCircle, FaExclamationTriangle, FaInbox, FaInfoCircle, FaTimesCircle } from 'react-icons/fa'
import { Button } from '../Button'
import { cx } from '../cx'
import s from './styles.module.scss'

// ---------------------------------------------------------------- StatusPill
export type PillTone = 'neutral' | 'accent' | 'good' | 'bad' | 'warn' | 'info' | 'outline'

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
    tone?: PillTone
    /** Leading coloured dot. */
    dot?: boolean
    icon?: ReactNode
    /** Fully rounded ends (counts). */
    round?: boolean
    size?: 'md' | 'sm'
    /** Uppercase micro-label styling. */
    upper?: boolean
    children: ReactNode
}

/** Small status chip. Tones are semantic, not decorative. */
export const StatusPill = ({ tone = 'neutral', dot, icon, round, size = 'md', upper, className, children, ...rest }: StatusPillProps) => (
    <span className={cx(s.pill, tone !== 'neutral' && s[tone], round && s.round, size === 'sm' && s.sm, upper && s.upper, className)} {...rest}>
        {dot && <span className={s.dot} aria-hidden="true" />}
        {icon}
        {children}
    </span>
)

const truthy = (v: unknown) => v === 1 || v === '1' || v === true || v === 'true'

/** Maps the case/client flags used across the dashboard to a label + tone. */
export const caseState = (flags: { cancelled?: unknown; duplicate?: unknown; marked_duplicate?: unknown }): { label: string; tone: PillTone } => {
    if (truthy(flags.duplicate) || truthy(flags.marked_duplicate)) return { label: 'Duplicate', tone: 'warn' }
    if (truthy(flags.cancelled)) return { label: 'Cancelled', tone: 'bad' }
    return { label: 'Active', tone: 'good' }
}

/** Ready-made pill for Active / Cancelled / Duplicate. */
export const CaseStatePill = (props: { cancelled?: unknown; duplicate?: unknown; marked_duplicate?: unknown; size?: 'md' | 'sm' }) => {
    const { label, tone } = caseState(props)
    return (
        <StatusPill tone={tone} dot size={props.size}>
            {label}
        </StatusPill>
    )
}

// -------------------------------------------------------------------- Banner
export type BannerTone = 'neutral' | 'good' | 'bad' | 'warn' | 'info'

export interface BannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    tone?: BannerTone
    title?: ReactNode
    icon?: ReactNode | false
    actions?: ReactNode
    children?: ReactNode
}

const BANNER_ICON: Record<BannerTone, ReactNode> = {
    neutral: <FaInfoCircle aria-hidden="true" />,
    info: <FaInfoCircle aria-hidden="true" />,
    good: <FaCheckCircle aria-hidden="true" />,
    warn: <FaExclamationTriangle aria-hidden="true" />,
    bad: <FaTimesCircle aria-hidden="true" />,
}

/** Inline message block: validation errors, warnings, success confirmations. */
export const Banner = ({ tone = 'neutral', title, icon, actions, className, children, ...rest }: BannerProps) => (
    <div className={cx(s.banner, tone !== 'neutral' && s[tone], className)} role={tone === 'bad' ? 'alert' : 'status'} {...rest}>
        {icon === false ? null : icon ?? BANNER_ICON[tone]}
        <div className={s.bannerBody}>
            {title && <span className={s.bannerTitle}>{title}</span>}
            {children}
        </div>
        {actions && <div className={s.bannerActions}>{actions}</div>}
    </div>
)

// ---------------------------------------------------------------- EmptyState
export type EmptyStateTone = 'neutral' | 'error' | 'loading'

export interface EmptyStateProps {
    tone?: EmptyStateTone
    title: ReactNode
    description?: ReactNode
    icon?: ReactNode
    /** Usually a retry or create button. */
    action?: ReactNode
    /** Drops the dashed card, for use inside an existing panel or table cell. */
    bare?: boolean
    className?: string
}

/** Placeholder for empty, failed and loading panels. */
export const EmptyState = ({ tone = 'neutral', title, description, icon, action, bare = false, className }: EmptyStateProps) => {
    const glyph = tone === 'loading' ? <Spinner size="md" /> : icon ?? (tone === 'error' ? <FaExclamationTriangle /> : <FaInbox />)
    return (
        <div className={cx(s.empty, bare && s.bare, tone === 'error' && s.error, className)} role="status">
            <div className={s.glyph}>{glyph}</div>
            <div className={s.title}>{title}</div>
            {description && <div className={s.desc}>{description}</div>}
            {action && <div className={s.action}>{action}</div>}
        </div>
    )
}

// ------------------------------------------------------------------- Spinner
export interface SpinnerProps {
    size?: 'sm' | 'md' | 'lg'
    /** Text next to the spinner. */
    text?: ReactNode
    /** Centre in a padded block (table bodies, cards). */
    block?: boolean
    className?: string
}

export const Spinner = ({ size = 'sm', text, block = false, className }: SpinnerProps) => {
    const glyph = <span className={cx(s.spinner, size !== 'sm' && s[size])} aria-hidden="true" />
    if (block) {
        return (
            <div className={cx(s.spinnerBlock, className)} role="status">
                {glyph}
                {text && <span>{text}</span>}
            </div>
        )
    }
    if (text) {
        return (
            <span className={cx(s.spinnerRow, className)} role="status">
                {glyph}
                {text}
            </span>
        )
    }
    return glyph
}

export interface LoadingOverlayProps {
    loading: boolean
    text?: ReactNode
    children: ReactNode
    className?: string
}

/** Wraps content and veils it with a spinner while `loading`. */
export const LoadingOverlay = ({ loading, text, children, className }: LoadingOverlayProps) => (
    <div className={cx(s.overlayHost, className)}>
        {children}
        {loading && (
            <div className={s.overlay} aria-busy="true">
                <Spinner size="md" />
                {text}
            </div>
        )}
    </div>
)

// --------------------------------------------------------------- KeyValueList
export interface KeyValueItem {
    label: ReactNode
    value: ReactNode
    mono?: boolean
}

export interface KeyValueListProps {
    items: KeyValueItem[]
    /** Wrap into responsive columns instead of a two-column grid. */
    columns?: boolean
    /**
     * One label-over-value row per item with hairline separators. Use in narrow
     * panels where values (emails, IDs) need the full width.
     */
    stacked?: boolean
    emptyText?: string
    className?: string
}

const isEmpty = (v: ReactNode) => v == null || v === '' || v === '—'

/** Label/value pairs for detail panels. */
export const KeyValueList = ({ items, columns = false, stacked = false, emptyText = '—', className }: KeyValueListProps) => (
    <dl className={cx(s.kv, (columns || stacked) && s.columns, stacked && s.stacked, className)}>
        {items.map((item, i) =>
            columns || stacked ? (
                <div key={i}>
                    <dt className={s.kvLabel}>{item.label}</dt>
                    <dd className={cx(s.kvValue, item.mono && s.mono, isEmpty(item.value) && s.kvEmpty)}>{isEmpty(item.value) ? emptyText : item.value}</dd>
                </div>
            ) : (
                <React.Fragment key={i}>
                    <dt className={s.kvLabel}>{item.label}</dt>
                    <dd className={cx(s.kvValue, item.mono && s.mono, isEmpty(item.value) && s.kvEmpty)}>{isEmpty(item.value) ? emptyText : item.value}</dd>
                </React.Fragment>
            )
        )}
    </dl>
)

// ------------------------------------------------------------------ CodeBlock
export interface CodeBlockProps {
    children: string
    maxHeight?: number
    className?: string
}

export const CodeBlock = ({ children, maxHeight, className }: CodeBlockProps) => (
    <pre className={cx(s.code, className)} style={maxHeight ? ({ ['--code-max' as any]: `${maxHeight}px` } as any) : undefined}>
        {children}
    </pre>
)

const truncateDeep = (value: any, maxString = 300): any => {
    if (typeof value === 'string') return value.length > maxString ? `${value.slice(0, maxString)}… [truncated ${value.length} chars]` : value
    if (Array.isArray(value)) return value.map((v) => truncateDeep(v, maxString))
    if (value && typeof value === 'object') {
        const out: Record<string, any> = {}
        for (const [k, v] of Object.entries(value)) out[k] = truncateDeep(v, maxString)
        return out
    }
    return value
}

export interface JsonViewerProps {
    title?: ReactNode
    data: any
    emptyMessage?: string
    maxHeight?: number
}

/** Pretty-printed JSON with copy and "show full values" controls. */
export const JsonViewer = ({ title, data, emptyMessage = 'No data', maxHeight }: JsonViewerProps) => {
    const [showFull, setShowFull] = useState(false)
    const [copied, setCopied] = useState(false)
    const rendered = useMemo(() => (data == null ? null : JSON.stringify(showFull ? data : truncateDeep(data), null, 2)), [data, showFull])

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        } catch {
            /* clipboard unavailable */
        }
    }

    return (
        <div>
            {(title || data != null) && (
                <div className={s.jsonHead}>
                    {title && <h5>{title}</h5>}
                    {data != null && (
                        <div className={s.jsonActions}>
                            <label>
                                <input type="checkbox" checked={showFull} onChange={(e) => setShowFull(e.target.checked)} />
                                Show full values
                            </label>
                            <Button size="sm" onClick={copy}>
                                {copied ? 'Copied' : 'Copy JSON'}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {rendered ? <CodeBlock maxHeight={maxHeight}>{rendered}</CodeBlock> : <p className={s.jsonEmpty}>{emptyMessage}</p>}
        </div>
    )
}
